import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { PlusCircleIcon } from 'lucide-react';
import TherapistCard from '../components/TherapistCard';
import axios from 'axios';
import { parseISO, format } from 'date-fns';
import { toast } from 'react-toastify';


const CATEGORY_OPTIONS = [
  { label: "Anxiety", category: "anxiety" },
  { label: "Depression", category: "depression" },
  { label: "Stress", category: "stress" },
  { label: "Sleep Issues", category: "sleep" },
  { label: "Work-life Balance", category: "work-life" },
  { label: "Teens", category: "teens" },
];

const MOOD_OPTIONS = ["Great", "Good", "Okay", "Down", "Stressed"];

const MoodButton = ({ label, onClick }) => (
  <Button
    variant="outline"
    className="text-sm px-3 py-1 rounded-full"
    onClick={onClick}
  >
    {label}
  </Button>
);



const ClientHome = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [showModal, setShowModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [therapists, setTherapists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [tips, setTips] = useState([]);
  const [quote, setQuote] = useState({ text: '', author: '' });

  const fetchTherapists = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access');
      const response = await axios.get(`/api/therapists/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTherapists(response.data);
    } catch (error) {
      console.error('Error fetching therapists:', error);
      toast.error("Failed to load therapists.");
    } finally {
      setLoading(false);
    }
  };

  const fetchSessions = async () => {
    try {
      const token = localStorage.getItem('access');
      const response = await axios.get('/api/bookings/?user_type=client', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSessions(response.data);
    } catch (error) {
      console.error('Error fetching sessions:', error);
      toast.error("Failed to load sessions.");
    }
  };

  useEffect(() => {
    fetchTherapists();
  }, []);

  useEffect(() => {
    fetchSessions();

    axios.get("https://quirkleai.com/api/wellness-tips/")
      .then((res) => setTips(res.data))
      .catch((err) => console.error("Error fetching wellness tips:", err));

    axios.get("https://quirkleai.com/api/motivation/")
      .then((res) => setQuote(res.data))
      .catch((err) => console.error("Error fetching quote:", err));
  }, []);

  return (
    <>
      {/* Header */}
      <div className="flex justify-between items-center flex-wrap gap-2">
        <h1 className="text-2xl font-semibold text-gray-900">Hi, {user?.username || "User"}</h1>
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="Search Quirkle AI"
            className="text-sm border rounded-md px-3 py-1.5 focus:outline-none"
          />
          <Button variant="secondary" className="px-3 py-2">🔍</Button>
        </div>
      </div>


      {/* Category Filter */}
      <Card className="shadow-sm border rounded-xl bg-white mt-6">
        <CardContent className="pt-6 px-6 pb-4">
          <h3 className="font-semibold mb-4">What brings you here today?</h3>
          <div className="flex gap-2 flex-wrap">
            {CATEGORY_OPTIONS.map((type) => (
              <MoodButton
                key={type.category}
                label={type.label}
                onClick={() => setSelectedCategory(type.category)}
              />
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Therapists Section */}
      <Card className="shadow-sm border rounded-xl bg-white mt-6">
        <CardContent className="pt-4">
          <h3 className="font-semibold mb-3">
            Our top therapists {selectedCategory ? `for ${selectedCategory}` : "available for you"}
          </h3>

          {loading ? (
            <p>Loading therapists...</p>
          ) : therapists.length === 0 ? (
            <p>No therapists found {selectedCategory && `for "${selectedCategory}"`}.</p>
          ) : (
            <div className="h-[300px] overflow-y-auto border rounded-lg p-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {therapists
                  .filter(therapist => !selectedCategory || (therapist.category && therapist.category === selectedCategory))
                  .map((therapist) => (
                    <TherapistCard
                      key={therapist.id}
                      therapist={therapist}
                    />
                  ))}
              </div>
            </div>
          )}

          <div className="mt-4">
            <Button variant="outline" onClick={() => setSelectedCategory('')}>
              View all therapists
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Sessions Section */}
      <Card className="shadow-sm border rounded-xl bg-white mt-6">
        <CardContent className="pt-4">
          <h3 className="font-semibold mb-2">Your next session</h3>
          {sessions.length > 0 ? (
            <ul className="text-sm text-gray-700 list-disc pl-4 space-y-1">
              {sessions.map((session) => (
                <li key={session.id}>
                  With <span className="font-medium">{session.therapist.username}</span> on{" "}
                  <span className="text-gray-500">{format(parseISO(session.start_time), 'PPP p')}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-600">No session scheduled!</p>
          )}

          <Button className="mt-2" onClick={() => navigate('/client/calendar')}>
            <PlusCircleIcon size={16} className="mr-2" />
            Schedule
          </Button>
        </CardContent>
      </Card>

      {/* Tips Section */}
      <Card className="shadow-sm border rounded-xl bg-white mt-6">
        <CardContent className="pt-4 pb-6">
          <h3 className="font-semibold mb-2">Daily Wellness Tips</h3>
          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            {tips.length > 0 ? (
              tips.map((tip, idx) => (
                <div key={idx} className="p-3 bg-orange-100 rounded-md">
                  <p className="font-semibold">{tip.title}</p>
                  <p>{tip.content}</p>
                </div>
              ))
            ) : (
              <p className="text-gray-500">No tips available for today.</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Quote Section */}
      <Card className="shadow-sm border rounded-xl bg-white mt-6">
        <CardContent className="pt-4 text-center text-orange-800 font-medium">
          {quote.text ? (
            <>
              <p className="italic">"{quote.text}"</p>
              <p className="mt-1 text-sm text-gray-500">– {quote.author}</p>
            </>
          ) : (
            <p className="text-gray-500">No quote available.</p>
          )}
        </CardContent>
      </Card>
    </>
  );
};

export default ClientHome;
